import { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import client from "@/lib/api";
import { money, fmtDate, statusLabel, STATUS_META } from "@/lib/format";
import { PageHeader, EmptyState, Skeletons, ErrorState } from "@/components/shared";
import CustomerDialog from "@/components/CustomerDialog";
import StageSelect from "@/components/StageSelect";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Search, ClipboardList, Building2, Download, Upload, LayoutGrid, Columns3, List, MoreHorizontal } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const CHIPS = [
  { key: "all", label: "All" },
  { key: "new", label: "New" },
  { key: "following_up", label: "Following up" },
  { key: "won", label: "Won" },
  { key: "booked", label: "Booked" },
  { key: "lost", label: "Lost" },
];

const VIEWS = [
  { key: "grid", label: "Grid view", icon: LayoutGrid },
  { key: "board", label: "Board view", icon: Columns3 },
  { key: "list", label: "List view", icon: List },
];

/* --- Board: compact, flat card. Stage lives in the column, shown as a colored left rail. --- */
function BoardCard({ c, onClick, onDragStart }) {
  const meta = STATUS_META[c.status] || {};
  return (
    <div
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter") onClick(); }}
      draggable
      onDragStart={onDragStart}
      data-testid={`customer-card-${c.id}`}
      style={{ borderLeftColor: meta.dot || "#94A3B8" }}
      className="group flex cursor-pointer flex-col gap-2 rounded-lg border border-l-4 border-slate-200 bg-white p-3 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md active:cursor-grabbing"
    >
      <div className="flex items-start justify-between gap-2">
        <h3 className="line-clamp-2 font-display text-sm font-semibold leading-tight tracking-tight text-ink">{c.name}</h3>
        <span className="shrink-0 font-display text-sm font-semibold text-ink">{money(c.total_value)}</span>
      </div>
      {c.company && (
        <p className="flex items-center gap-1 truncate text-xs text-slate-500">
          <Building2 className="h-3 w-3 shrink-0" /> {c.company}
        </p>
      )}
      <div className="mt-1 flex items-center justify-between border-t border-slate-100 pt-2 text-xs text-slate-400">
        <span className="flex items-center gap-1">
          <ClipboardList className="h-3 w-3" /> {c.enquiry_count} {c.enquiry_count === 1 ? "enquiry" : "enquiries"}
        </span>
        <span>{fmtDate(c.last_visit, "d MMM")}</span>
      </div>
    </div>
  );
}

/* --- Grid: spacious card with the interactive stage pill. --- */
function GridCard({ c, onClick }) {
  return (
    <div
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter") onClick(); }}
      data-testid={`customer-card-${c.id}`}
      className="group flex cursor-pointer flex-col rounded-xl border border-slate-200 bg-white p-5 text-left shadow-[0_2px_12px_rgba(35,52,90,0.04)] transition-all hover:-translate-y-0.5 hover:border-primary/20 hover:shadow-[0_8px_24px_rgba(35,52,90,0.08)]"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="line-clamp-2 font-display text-base font-semibold leading-tight tracking-tight text-ink">{c.name}</h3>
          {c.company && (
            <p className="mt-0.5 flex items-center gap-1 truncate text-sm text-slate-500">
              <Building2 className="h-3.5 w-3.5 shrink-0" /> {c.company}
            </p>
          )}
        </div>
        <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
          <StageSelect customerId={c.id} status={c.status} />
        </span>
      </div>
      <div className="mt-4 flex items-end justify-between border-t border-slate-100 pt-4">
        <div className="flex items-center gap-1.5 text-xs text-slate-500">
          <ClipboardList className="h-3.5 w-3.5" />
          {c.enquiry_count} {c.enquiry_count === 1 ? "enquiry" : "enquiries"}
          <span className="text-slate-300">·</span>
          <span>{fmtDate(c.last_visit, "d MMM")}</span>
        </div>
        <div className="font-display text-xl font-semibold tracking-tight text-ink">{money(c.total_value)}</div>
      </div>
    </div>
  );
}

/* --- List: dense, scannable row for hundreds of leads. --- */
function ListRow({ c, onClick }) {
  return (
    <div
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter") onClick(); }}
      data-testid={`customer-row-${c.id}`}
      className="flex cursor-pointer items-center gap-3 border-b border-slate-100 px-4 py-3 transition-colors last:border-0 hover:bg-slate-50"
    >
      <div className="min-w-0 flex-1">
        <div className="truncate font-display text-sm font-semibold text-ink">{c.name}</div>
        {c.company && <div className="truncate text-xs text-slate-500 sm:hidden">{c.company}</div>}
      </div>
      <div className="hidden w-40 shrink-0 truncate text-xs text-slate-500 sm:block">{c.company || "—"}</div>
      <div className="hidden w-36 shrink-0 text-xs text-slate-400 md:block">
        {c.enquiry_count} enq · {fmtDate(c.last_visit, "d MMM")}
      </div>
      <div className="shrink-0" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
        <StageSelect customerId={c.id} status={c.status} />
      </div>
      <div className="w-20 shrink-0 text-right font-display text-sm font-semibold text-ink">{money(c.total_value)}</div>
    </div>
  );
}

export default function Pipeline() {
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [dialog, setDialog] = useState(false);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [view, setView] = useState("grid");

  const moveLead = async (id, newStatus) => {
    const lead = customers.find((c) => c.id === id);
    if (!lead || lead.status === newStatus) return;
    try {
      await client.put(`/customers/${id}`, { status: newStatus });
      toast.success(`${lead.name} moved to ${statusLabel(newStatus)}`);
      qc.invalidateQueries({ queryKey: ["customers"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["calendar"] });
    } catch {
      toast.error("Could not move lead");
    }
  };

  const exportCsv = () => {
    const rows = customers.map((c) => ({
      name: c.name, phone: c.phone || "", email: c.email || "", company: c.company || "",
      status: c.status, follow_up_date: c.follow_up_date || "", enquiries: c.enquiry_count,
      total_value: c.total_value, notes: (c.notes || "").replace(/\n/g, " "),
    }));
    const headers = ["name", "phone", "email", "company", "status", "follow_up_date", "enquiries", "total_value", "notes"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [headers.join(","), ...rows.map((r) => headers.map((h) => esc(r[h])).join(","))].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = `tilesmate-leads-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} leads`);
  };

  const parseCsv = (text) => {
    const lines = text.split(/\r?\n/).filter((l) => l.trim());
    if (!lines.length) return [];
    const split = (line) => {
      const out = []; let cur = ""; let q = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') { if (q && line[i + 1] === '"') { cur += '"'; i++; } else q = !q; }
        else if (ch === "," && !q) { out.push(cur); cur = ""; }
        else cur += ch;
      }
      out.push(cur); return out;
    };
    const header = split(lines[0]).map((h) => h.trim().toLowerCase());
    return lines.slice(1).map((line) => {
      const cells = split(line);
      const row = {};
      header.forEach((h, i) => { row[h] = (cells[i] || "").trim(); });
      return { name: row.name, phone: row.phone, email: row.email, company: row.company, status: row.status, notes: row.notes };
    }).filter((r) => r.name);
  };

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      const rows = parseCsv(text);
      if (!rows.length) { toast.error("No valid rows found. Need a 'name' column."); return; }
      const { data } = await client.post("/customers/import", { rows });
      toast.success(`Imported ${data.created} leads`);
      qc.invalidateQueries({ queryKey: ["customers"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const stats = useQuery({
    queryKey: ["stats"],
    queryFn: async () => (await client.get("/customers/stats")).data,
  });

  const list = useQuery({
    queryKey: ["customers", view === "board" ? "all" : status, search],
    queryFn: async () =>
      (await client.get("/customers", { params: { status: view === "board" ? "all" : status, search: search || undefined } })).data,
  });

  const customers = list.data || [];

  return (
    <div className="animate-fade-up">
      <PageHeader title="Pipeline" subtitle="Every showroom walk-in and active deal" testid="pipeline-header">
        <div className="mr-1 flex items-center rounded-lg border border-border bg-white p-0.5">
          {VIEWS.map((v) => (
            <button
              key={v.key}
              onClick={() => setView(v.key)}
              data-testid={`view-${v.key}-btn`}
              title={v.label}
              aria-label={v.label}
              className={cn(
                "grid h-8 w-8 place-items-center rounded-md transition-colors",
                view === v.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-ink"
              )}
            >
              <v.icon className="h-4 w-4" />
            </button>
          ))}
        </div>
        <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onImportFile} className="hidden" data-testid="import-file-input" />
        <Button onClick={() => fileRef.current?.click()} disabled={importing} variant="outline" data-testid="import-csv-btn" className="hidden gap-2 sm:inline-flex"><Upload className="h-4 w-4" />{importing ? "Importing…" : "Import"}</Button>
        <Button onClick={exportCsv} variant="outline" data-testid="export-csv-btn" className="hidden gap-2 sm:inline-flex"><Download className="h-4 w-4" />Export</Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon" className="sm:hidden" data-testid="pipeline-more-btn" aria-label="More actions"><MoreHorizontal className="h-4 w-4" /></Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => fileRef.current?.click()} data-testid="import-csv-mobile"><Upload className="mr-2 h-4 w-4" />Import CSV</DropdownMenuItem>
            <DropdownMenuItem onClick={exportCsv} data-testid="export-csv-mobile"><Download className="mr-2 h-4 w-4" />Export CSV</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Button onClick={() => setDialog(true)} data-testid="add-customer-btn" className="gap-2">
          <Plus className="h-4 w-4" /> New<span className="hidden sm:inline"> walk-in</span>
        </Button>
      </PageHeader>

      {/* Search + chips */}
      <div className="mb-6 space-y-4">
        <div className="relative max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="pipeline-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, phone or company…"
            className="pl-9"
          />
        </div>
        <div className={cn("no-scrollbar flex gap-2 overflow-x-auto pb-1", view === "board" && "hidden")}>
          {CHIPS.map((chip) => {
            const active = status === chip.key;
            const count = stats.data?.[chip.key];
            return (
              <button
                key={chip.key}
                onClick={() => setStatus(chip.key)}
                data-testid={`chip-${chip.key}`}
                className={cn(
                  "flex shrink-0 items-center gap-2 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors",
                  active
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-white text-muted-foreground hover:text-ink"
                )}
              >
                {chip.label}
                {count != null && (
                  <span className={cn("rounded-full px-1.5 text-[11px] font-semibold", active ? "bg-white/20" : "bg-muted")}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {list.isError ? (
        <ErrorState onRetry={() => { list.refetch(); stats.refetch(); }} message="We couldn't load your pipeline. Check your connection and try again." />
      ) : list.isLoading ? (
        <Skeletons />
      ) : view === "board" ? (
        <div className="no-scrollbar -mx-1 flex snap-x snap-mandatory gap-3 overflow-x-auto px-1 pb-2" data-testid="kanban-board">
          {CHIPS.filter((c) => c.key !== "all").map((col) => {
            const colCustomers = customers.filter((c) => c.status === col.key);
            return (
              <div
                key={col.key}
                data-testid={`kanban-column-${col.key}`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { const id = e.dataTransfer.getData("text/plain"); if (id) moveLead(id, col.key); }}
                className="flex w-[85vw] max-w-[320px] shrink-0 snap-center flex-col rounded-2xl border border-slate-100 bg-slate-50/70 p-2 sm:w-[300px]"
              >
                <div className="mb-2 flex items-center justify-between px-2 pt-1">
                  <span className="flex items-center gap-1.5 text-sm font-semibold text-ink">
                    <span className="h-2 w-2 rounded-full" style={{ background: STATUS_META[col.key]?.dot }} />{col.label}
                  </span>
                  <span className="rounded-full bg-white px-1.5 text-[11px] font-semibold text-muted-foreground">{colCustomers.length}</span>
                </div>
                <div className="flex min-h-[120px] flex-col gap-2">
                  {colCustomers.map((c) => (
                    <BoardCard key={c.id} c={c} onClick={() => navigate(`/customers/${c.id}`)}
                      onDragStart={(e) => e.dataTransfer.setData("text/plain", c.id)} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : customers.length === 0 ? (
        <EmptyState
          icon={ClipboardList}
          title={search ? "No matches found" : `No customers in ${statusLabel(status)}`}
          hint="Add a walk-in to start tracking this deal through your pipeline."
          action={
            <Button onClick={() => setDialog(true)} data-testid="empty-add-customer-btn" className="gap-2">
              <Plus className="h-4 w-4" /> Add customer
            </Button>
          }
        />
      ) : view === "list" ? (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="pipeline-list">
          {customers.map((c) => (
            <ListRow key={c.id} c={c} onClick={() => navigate(`/customers/${c.id}`)} />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {customers.map((c) => (
            <GridCard key={c.id} c={c} onClick={() => navigate(`/customers/${c.id}`)} />
          ))}
        </div>
      )}

      <CustomerDialog
        open={dialog}
        onOpenChange={setDialog}
        onSaved={() => {
          qc.invalidateQueries({ queryKey: ["customers"] });
          qc.invalidateQueries({ queryKey: ["stats"] });
        }}
      />
    </div>
  );
}
